import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaBars, FaTimes } from "react-icons/fa";

interface PlatformHeaderProps {
 isSticky?: boolean;
}

const PlatformHeader: React.FC<PlatformHeaderProps> = ({ isSticky = false }) => {
 const { t } = useTranslation();
 const location = useLocation();
 const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
 const [scrolled, setScrolled] = useState(false);

 useEffect(() => {
  const handleScroll = () => {
   setScrolled(window.scrollY > 50);
  };
  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
 }, []);

 const navItems = [
  { path: "/", label: t("platform.nav.home") || "Home" },
  { path: "/how-it-works", label: t("platform.nav.howItWorks") || "How It Works" },
  { path: "/pricing", label: t("platform.nav.pricing") || "Pricing" },
  { path: "/policies", label: t("platform.nav.policies") || "Policies" },
  { path: "/contact", label: t("platform.nav.contact") || "Contact Us" },
 ];

 const isActive = (path: string) => {
  if (path === "/") {
   return location.pathname === "/";
  }
  return location.pathname.startsWith(path);
 };

 return (
  <header
   className={`w-full transition-all duration-300 ${
    isSticky && scrolled
     ? "fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md "
     : isSticky
     ? "absolute top-0 left-0 right-0 z-50 bg-transparent"
     : "relative bg-transparent"
   }`}
  >
   <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex items-center justify-between h-16 md:h-20">
     {/* Logo */}
     <Link to="/" className="flex items-center gap-3 space-x-3 group">
      <img 
       src="/logo.png" 
       alt="uShoplify" 
       className="h-10 w-auto md:h-12 transform group- transition-transform"
      />
      <span
       className={`font-bold text-xl md:text-2xl transition-colors ${
        isSticky && scrolled ? "text-gray-900" : "text-white"
       }`}
      >
       {t("platform.brand.name") || "uShoplify"}
      </span>
     </Link>

     {/* Desktop Navigation */}
     <div className="hidden md:flex items-center space-x-1">
      {navItems.map((item) => (
       <Link
        key={item.path}
        to={item.path}
        className={`px-4 py-2 font-medium transition-all ${
         isActive(item.path)
          ? isSticky && scrolled
           ? "bg-blue-600 text-white"
           : "bg-white/20 text-white backdrop-blur-sm"
          : isSticky && scrolled
          ? "text-gray-700 hover:bg-gray-100"
          : "text-white/90 hover:bg-white/10 hover:text-white"
        }`}
       >
        {item.label}
       </Link>
      ))}
      <Link
       to="/onboarding"
       className={`ml-4 px-6 py-2 font-bold transition-all transform ${
        isSticky && scrolled
         ? "bg-blue-600 text-white hover:bg-blue-700 "
         : "bg-white text-blue-600 hover:bg-gray-100 "
       }`}
      >
       {t("platform.nav.getStarted") || "Get Started"}
      </Link>
     </div>

     {/* Mobile Menu Button */}
     <div className="md:hidden flex items-center space-x-3">
      <button
       onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
       className={`p-2 transition-colors ${
        isSticky && scrolled
         ? "text-gray-700 hover:bg-gray-100"
         : "text-white hover:bg-white/10"
       }`}
       aria-label="Toggle menu"
      >
       {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
      </button>
     </div>
    </div>

    {/* Mobile Menu */}
    <div
     className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
      mobileMenuOpen
       ? "max-h-96 opacity-100"
       : "max-h-0 opacity-0"
     }`}
    >
     <div
      className={`py-4 space-y-2 ${
       isSticky && scrolled ? "bg-white" : "bg-white/95 backdrop-blur-md"
      } mt-2 transform transition-transform duration-300 ${
       mobileMenuOpen ? "translate-y-0" : "-translate-y-4"
      }`}
     >
      {navItems.map((item, index) => (
       <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileMenuOpen(false)}
        className={`block px-4 py-2 font-medium transition-all duration-200 ${
         isActive(item.path)
          ? "bg-blue-600 text-white"
          : "text-gray-700 hover:bg-gray-100"
        }`}
        style={{
         animationDelay: `${index * 50}ms`,
         animation: mobileMenuOpen ? "slideInDown 0.3s ease-out forwards" : "none",
        }}
       >
        {item.label}
       </Link>
      ))}
      <Link
       to="/onboarding"
       onClick={() => setMobileMenuOpen(false)}
       className="block mx-4 mt-4 px-6 py-3 bg-blue-600 text-white font-bold text-center hover:bg-blue-700 transition-all duration-200 transform "
       style={{
        animationDelay: `${navItems.length * 50}ms`,
        animation: mobileMenuOpen ? "slideInDown 0.3s ease-out forwards" : "none",
       }}
      >
       {t("platform.nav.getStarted") || "Get Started"}
      </Link>
     </div>
    </div>
   </nav>
  </header>
 );
};

export default PlatformHeader;

